import React from 'react';
import {
  Typography, Box, Container, Grid, Card, CardContent, Button,
  useTheme, alpha, Chip, Stack, Avatar, Paper
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Inventory as InventoryIcon,
  AttachMoney as FinanceIcon, People as HRIcon,
  Settings as AutomationIcon, Security as SecurityIcon,
  ShoppingCart as SalesIcon, LocalShipping as LogisticsIcon,
  NotificationsActive as AlertIcon, CalendarToday as CalendarIcon,
  Business as BusinessIcon, TrendingUp as AnalyticsIcon,
  CloudQueue as CloudIcon, MobileFriendly as MobileIcon,
  VerifiedUser as ComplianceIcon, Sync as SyncIcon,
  GroupWork as IntegrationIcon, Star as StarIcon,
  CheckCircle as CheckCircleIcon, ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import HomeFooter from "../HomeFooter";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

// Animation variants (reduced for performance)
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

const FeatureCard = ({ icon, title, description, color, features }) => (
  <motion.div variants={fadeInUp} whileHover={{ y: -5 }} style={{ height: '100%' }}>
    <Card sx={{
      height: '100%',
      borderRadius: 3,
      background: 'white',
      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
      transition: 'all 0.3s ease',
      '&:hover': { boxShadow: `0 15px 30px ${alpha(color || '#3b82f6', 0.15)}` }
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Avatar sx={{ bgcolor: alpha(color || '#3b82f6', 0.1), color: color || '#3b82f6' }}>
            {icon}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e3a8a', fontSize: { xs: '1.1rem', sm: '1.2rem' } }}>
            {title}
          </Typography>
        </Box>
        <Typography sx={{ color: '#64748b', mb: 2, fontSize: '0.95rem' }}>
          {description}
        </Typography>
        {features && (
          <Box component="ul" sx={{ pl: 0, mb: 0 }}>
            {features.slice(0, 3).map((feature, idx) => (
              <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, listStyle: 'none' }}>
                <CheckCircleIcon sx={{ color: color || '#3b82f6', fontSize: 18, mr: 1 }} />
                <Typography variant="body2" sx={{ color: '#475569' }}>{feature}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

const colors = [
  { color: "#8b5cf6", bg: "#f5f3ff" }, // purple
  { color: "#10b981", bg: "#ecfdf5" }, // green
  { color: "#ef4444", bg: "#fef2f2" }, // red
  { color: "#3b82f6", bg: "#eff6ff" }, // blue
  { color: "#f59e0b", bg: "#fffbeb" }, // yellow
  { color: "#06b6d4", bg: "#ecfeff" }, // cyan
  { color: "#6366f1", bg: "#eef2ff" }, // indigo
  { color: "#84cc16", bg: "#f7fee7" }  // lime
];

const SimpleCard = ({ icon, title, description, color }) => (
  <motion.div variants={fadeInUp} whileHover={{ y: -5 }} style={{ height: '100%' }}>
    <Paper sx={{
      p: { xs: 2, sm: 3 },
      height: '100%',
      borderRadius: 2,
      background: 'white',
      boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
      textAlign: 'center'
    }}>
      <Avatar sx={{ bgcolor: alpha(color || '#3b82f6', 0.1), color: color || '#3b82f6', width: 50, height: 50, mx: 'auto', mb: 2 }}>
        {icon}
      </Avatar>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e3a8a', mb: 1, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b' }}>
        {description}
      </Typography>
    </Paper>
  </motion.div>
);

function ERPSystem() {
  const navigate = useNavigate();

  // Simplified data arrays
  const featureCards = [
    { title: "Real-time Dashboard", description: "Live KPIs, revenue trends and performance metrics.", icon: <DashboardIcon />, color: "#3b82f6", features: ["Live metrics", "Custom widgets", "Mobile views"] },
    { title: "Finance & Accounting", description: "Automated billing, expense tracking and reporting.", icon: <FinanceIcon />, color: "#f59e0b", features: ["Auto invoicing", "Expense tracking", "Tax compliance"] },
    { title: "HR & Payroll", description: "Payroll, attendance and employee management.", icon: <HRIcon />, color: "#8b5cf6", features: ["Auto payroll", "Leave management", "Self-service"] },
    { title: "Sales & CRM", description: "Lead tracking, order processing and customer history.", icon: <SalesIcon />, color: "#ef4444", features: ["Lead tracking", "Order processing", "Sales analytics"] },
    { title: "Workflow Automation", description: "Automate processes with approval workflows.", icon: <AutomationIcon />, color: "#06b6d4", features: ["Approval flows", "Task automation", "Process optimization"] }
  ];

  const additionalFeatures = [
    { title: "Employee Management", description: "Provide quick assistance and resolve customer queries efficiently.", icon: <CalendarIcon />, color: "#8b5cf6" },
    { title: "Customer Supporting", description: "Provide quick assistance and resolve customer queries efficiently.", icon: <BusinessIcon />, color: "#10b981" },
    { title: "Low-Stock Alerts", description: "Get notified when stock runs low.", icon: <AlertIcon />, color: "#ef4444" },
    { title: "Role-Based Access", description: "Granular permissions for security.", icon: <SecurityIcon />, color: "#3b82f6" },
    { title: "Analytics", description: "Advanced reporting and insights.", icon: <AnalyticsIcon />, color: "#f59e0b" },
    { title: "Mobile Access", description: "Access from anywhere.", icon: <MobileIcon />, color: "#06b6d4" },
    { title: "Cloud Hosting", description: "Secure cloud infrastructure.", icon: <CloudIcon />, color: "#6366f1" },
    { title: "Easy Integration", description: "Connect with existing tools.", icon: <IntegrationIcon />, color: "#84cc16" }
  ];

  const metrics = [
    { value: '98%', label: 'Satisfaction', icon: '😊' },
    // { value: '500+', label: 'Companies', icon: '🏢' },
    { value: '24/7', label: 'Support', icon: '🎯' }
  ];

  const quickFeatures = [
    { icon: '📊', title: 'Real-time Analytics', color: '#0284c7' },
    { icon: '📦', title: 'Inventory', color: '#8b5cf6' },
    { icon: '💰', title: 'Finance', color: '#db2777' },
    { icon: '👥', title: 'HR', color: '#059669' }
  ];

  const benefitCards = [
    { icon: '🚀', title: 'Quick Implementation', desc: 'Go live in weeks', color: '#0284c7' },
    { icon: '🛡️', title: 'Bank-Level Security', desc: 'Data always protected', color: '#8b5cf6' },
    { icon: '⚡', title: 'Automated Workflows', desc: 'Reduce manual work', color: '#db2777' },
    { icon: '🌍', title: 'Global Ready', desc: 'Multi-currency', color: '#059669' }
  ];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Navbar />

      {/* Hero Section - Updated Background Colour */}
      <Box
        sx={{
          // Changed background to match your image (light blue-grey gradient)
          background: "linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)",
          color: "#0f172a",
          py: { xs: 6, md: 10 },
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Background Glow Effects */}
        <Box
          sx={{
            position: "absolute",
            top: "-100px",
            right: "-100px",
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(59,130,246,0.15), transparent 70%)",
            filter: "blur(80px)"
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-50px",
            left: "-50px",
            width: "300px",
            height: "300px",
            background: "radial-gradient(circle, rgba(99,102,241,0.12), transparent 70%)",
            filter: "blur(60px)"
          }}
        />

        {/* Floating Elements */}
        {[...Array(8)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: `${Math.random() * 80 + 10}%`,
              left: `${Math.random() * 80 + 10}%`,
              width: `${Math.random() * 10 + 5}px`,
              height: `${Math.random() * 10 + 5}px`,
              background: i % 2 === 0 ? "#3b82f6" : "#6366f1",
              borderRadius: "50%",
              opacity: 0.1,
              filter: "blur(2px)",
              animation: `float ${Math.random() * 6 + 4}s infinite ease-in-out`
            }}
          />
        ))}

        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">

            {/* LEFT SIDE - ENHANCED CONTENT */}
            <Grid item xs={12} md={6} mt={3}>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                {/* Logo + Main Title in Same Row */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {/* Logo */}
                  <Box
                    component="img"
                    src="/images/cloud_new.png"
                    alt="LIDER ERP Logo"
                    sx={{
                      width: { xs: 60, md: 80 },
                      height: { xs: 60, md: 80 },
                      objectFit: "contain",
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.7)",
                      p: 0.5,
                      boxShadow: "0 8px 20px rgba(59,130,246,0.15)",
                    }}
                  />

                  {/* Main Title */}
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: "2.2rem", sm: "2.8rem", md: "3.5rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    LIDER ERP
                  </Typography>
                </Stack><br></br>

                {/* Enhanced Subtitle with Key Benefits */}
                <Typography
                  sx={{
                    color: "#475569",
                    mb: 3,
                    fontSize: "1.4rem",
                    maxWidth: "520px"
                  }}
                >
                  All-in-one ERP solution for finance, inventory, HR and operations.
                  Manage everything from one powerful platform with real-time insights.
                </Typography>

                {/* Feature Pills */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                  {["✅ Real-time Analytics", "✅ Cloud-based", "✅ 24/7 Support", "✅ Secure"].map((feature, idx) => (
                    <Chip
                      key={idx}
                      label={feature}
                      size="small"
                      sx={{
                        background: "white",
                        color: "#1e293b",
                        fontWeight: 500,
                        border: "1px solid #e2e8f0"
                      }}
                    />
                  ))}
                </Box><br></br>

                {/* Metrics Grid */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {metrics.map((metric, idx) => (
                    <Grid item xs={4} key={idx}>
                      <Box sx={{ textAlign: "center", p: 1.5, background: "white", borderRadius: 2, boxShadow: "0 4px 10px rgba(0,0,0,0.02)" }}>
                        <Typography fontSize="2rem" lineHeight={1}>{metric.icon}</Typography>
                        <Typography sx={{ fontWeight: 800, color: "#2563eb", fontSize: "1.3rem" }}>
                          {metric.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {metric.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Trusted Companies Bar */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                    TRUSTED BY:
                  </Typography>
                  {["TechCorp", "Innovate", "GlobalInc", "FutureLabs"].map((company, idx) => (
                    <Typography key={idx} variant="body2" sx={{ color: "#94a3b8", fontWeight: 500, color: "#1e293b" }}>
                      {company}
                    </Typography>
                  ))}
                </Box>

              </motion.div>
            </Grid>

            {/* RIGHT SIDE - ENHANCED WITH MORE CONTENT */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    background: "rgba(255,255,255,0.7)",
                    backdropFilter: "blur(20px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.08)"
                  }}
                >
                  {/* Header with Live Indicator */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700,textTransform:'uppercase' }}>
                      Platform Overview
                    </Typography>
                  </Box>

                  {/* Feature Grid - Enhanced */}
                  <Grid container spacing={1.5}>
                    {quickFeatures.map((feature, idx) => (
                      <Grid item xs={6} key={idx}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1.5,
                            borderRadius: 2,
                            background: "#f1f5f9",
                            transition: "0.3s",
                            "&:hover": {
                              background: "#e0f2fe",
                              transform: "translateX(3px)"
                            }
                          }}
                        >
                          <Typography fontSize="1.3rem">{feature.icon}</Typography>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {feature.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                              {feature.desc}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Benefit Cards - Enhanced */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 2 }}>
                    Why Choose LIDER ERP
                  </Typography>
                  <Grid container spacing={1.5}>
                    {benefitCards.map((card, idx) => (
                      <Grid item xs={6} key={idx}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            background: "white",
                            boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            transition: "0.3s",
                            height: "100%",
                            "&:hover": {
                              transform: "translateY(-5px)",
                              boxShadow: `0 15px 25px ${card.color}20`
                            }
                          }}
                        >
                          <Typography fontSize="2rem" mb={1}>{card.icon}</Typography>
                          <Typography fontWeight={700} fontSize="0.95rem">
                            {card.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {card.desc}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Integration Stats */}
                  <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {/* <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: "#f1f5f9", textAlign: "center" }}>
                      <Typography fontWeight={700} color="#2563eb">10+</Typography>
                      <Typography variant="caption">Integrations</Typography>
                    </Box> */}
                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: "#f1f5f9", textAlign: "center" }}>
                      <Typography fontWeight={700} color="#2563eb">99.9%</Typography>
                      <Typography variant="caption">Uptime</Typography>
                    </Box>
                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: "#f1f5f9", textAlign: "center" }}>
                      <Typography fontWeight={700} color="#2563eb">24/7</Typography>
                      <Typography variant="caption">Support</Typography>
                    </Box>
                  </Box>

                </Paper>
              </motion.div>
            </Grid>

          </Grid>
        </Container>

        {/* CSS Animations */}
        <style>
          {`
      @keyframes float {
        0%, 100% { transform: translateY(0) translateX(0); }
        50% { transform: translateY(-15px) translateX(5px); }
      }
    `}
        </style>
      </Box>


      {/* Core Modules */}
      <Box sx={{ py: { xs: 4, md: 8 }, background: '#f8fafc' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e3a8a', mb: 1,textTransform:'uppercase', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                Everything Your Business Needs
              </Typography>
              <Typography sx={{ color: '#64748b', maxWidth: '700px', mx: 'auto', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Comprehensive modules working seamlessly together.
              </Typography>
            </Box>

            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: {
                  xs: 'center',
                  sm: featureCards.length === 5 ? 'center' : 'flex-start',
                  md: featureCards.length % 3 === 2 ? 'center' : 'flex-start'
                }
              }}
            >
              {featureCards.map((card, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Box sx={{ width: '100%', maxWidth: '400px' }}>
                    <FeatureCard {...card} />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Advanced Features */}
      <Box sx={{ py: { xs: 4, md: 8 }, background: 'white' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, textTransform:'uppercase', color: '#1e3a8a', mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                Advanced Features
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {additionalFeatures.map((feature, index) => {
                // Get color for this card - either from feature or use default colors array
                const cardColor = feature.color || colors[index % colors.length].color;
                const cardBg = colors[index % colors.length]?.bg || alpha(cardColor, 0.1);

                return (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div
                      variants={fadeInUp}
                      whileHover={{ y: -5, scale: 1.02 }}
                      style={{ height: '100%' }}
                      custom={index}
                    >
                      <Paper sx={{
                        p: { xs: 2, sm: 3 },
                        height: '100%',
                        borderRadius: 3,
                        background: 'white',
                        boxShadow: `0 5px 20px ${alpha(cardColor, 0.15)}`,
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        border: `1px solid ${alpha(cardColor, 0.2)}`,
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          boxShadow: `0 15px 35px ${alpha(cardColor, 0.25)}`,
                          transform: 'translateY(-8px)',
                          '& .feature-icon': {
                            transform: 'scale(1.1) rotate(5deg)',
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: `linear-gradient(90deg, ${cardColor}, ${alpha(cardColor, 0.5)})`,
                        }
                      }}>
                        {/* Icon with colored background */}
                        <Box
                          className="feature-icon"
                          sx={{
                            width: { xs: 60, sm: 70 },
                            height: { xs: 60, sm: 70 },
                            borderRadius: '50%',
                            background: alpha(cardColor, 0.12),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            transition: 'all 0.3s ease',
                            border: `2px solid ${alpha(cardColor, 0.2)}`,
                            boxShadow: `0 8px 16px ${alpha(cardColor, 0.15)}`
                          }}
                        >
                          <Avatar sx={{
                            bgcolor: 'transparent',
                            color: cardColor,
                            width: 40,
                            height: 40,
                            '& svg': {
                              fontSize: '2rem'
                            }
                          }}>
                            {feature.icon}
                          </Avatar>
                        </Box>

                        {/* Title with feature color */}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 800,
                            color: cardColor,
                            mb: 1.5,
                            fontSize: { xs: '1rem', sm: '1.15rem' },
                            letterSpacing: '0.5px'
                          }}
                        >
                          {feature.title}
                        </Typography>

                        {/* Description */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#475569',
                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                            lineHeight: 1.6,
                            px: 1
                          }}
                        >
                          {feature.description}
                        </Typography>

                        {/* Decorative dot pattern */}
                        <Box sx={{
                          position: 'absolute',
                          bottom: 5,
                          right: 5,
                          display: 'flex',
                          gap: 0.5,
                          opacity: 0.1
                        }}>
                          {[1, 2, 3].map((dot) => (
                            <Box key={dot} sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: cardColor
                            }} />
                          ))}
                        </Box>
                      </Paper>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* CTA Section */}
      {/* <Box sx={{ py: { xs: 4, md: 6 }, background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center', px: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Join 500+ companies using LIDER ERP
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => (window.location.href = "/contact")}
            sx={{
              background: "white",
              color: "#1e3a8a",
              fontWeight: 700,
              px: { xs: 4, sm: 6 },
              "&:hover": {
                background: "#f8fafc",
              },
            }}
          >
            Get Started Today
          </Button>
        </Container>
      </Box> */}

      <HomeFooter />
    </Box>
  );
}

export default ERPSystem;